import React, { useState, useEffect, useRef } from 'react';
import { getUserName } from '../../utils/authUtils.ts';
import { make_question_request } from '../../services/api.js';
import { nanoid } from 'nanoid';
import { io } from 'socket.io-client';

// Function to get Zendesk credentials from localStorage
const getZendeskCredentials = () => {
  try {
    const savedCredentials = localStorage.getItem('zendeskCredentials');
    if (savedCredentials) {
      return JSON.parse(savedCredentials);
    }
  } catch (error) {
    console.error('Error parsing Zendesk credentials:', error);
  }
  return null;
};

// Credentials Modal Component
const CredentialsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Zendesk Credentials Required</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="mb-6">
          <div className="flex items-center mb-3">
            <i className="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
            <p className="text-gray-300">
              Please configure your Zendesk credentials first to connect with a human agent.
            </p>
          </div>
          
          <div className="bg-gray-700 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-300 mb-2">You need to provide:</p>
            <ul className="text-sm text-gray-400 space-y-1">
              <li>• Zendesk subdomain</li>
              <li>• Email address</li>
              <li>• API token</li>
            </ul>
          </div>
        </div>
        
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const useChat = () => {
  const [messages, setMessages] = useState([
    { id: 1, text: "Hello! How can I assist you today?", sender: "ai", timestamp: "11:32 PM" },
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(nanoid());
  
  // Zendesk integration state
  const [isConnected, setIsConnected] = useState(false);
  const [ticketId, setTicketId] = useState(null);
  const [isHumanMode, setIsHumanMode] = useState(false);
  const [isConnectingToAgent, setIsConnectingToAgent] = useState(false);
  const [zendeskConfigured, setZendeskConfigured] = useState(false);
  const [credentialsAvailable, setCredentialsAvailable] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  
  const inputRef = useRef(null);
  const socketRef = useRef(null);

  // Check for credentials availability
  useEffect(() => {
    const credentials = getZendeskCredentials();
    setCredentialsAvailable(!!credentials && credentials.subdomain && credentials.email && credentials.api_token);
  }, []);

  // Initialize WebSocket connection for Zendesk
  useEffect(() => {
    const BACKEND_URL = 'http://localhost:5001'; // Replace with your backend URL
    
    socketRef.current = io(BACKEND_URL, {
      transports: ['websocket', 'polling'],
      timeout: 20000,
    });
    
    const socket = socketRef.current;

    // Connection event handlers
    socket.on('connect', () => {
      console.log('Connected to Zendesk server');
      setIsConnected(true);
      
      // Configure Zendesk credentials immediately after connection if available
      const credentials = getZendeskCredentials();
      if (credentials && credentials.subdomain && credentials.email && credentials.api_token) {
        socket.emit('configure_zendesk', credentials);
      }
    });

    socket.on('disconnect', () => {
      console.log('Disconnected from Zendesk server');
      setIsConnected(false);
      setZendeskConfigured(false);
    });

    socket.on('connect_error', (error) => {
      console.error('Zendesk connection error:', error);
    });

    // Zendesk configuration response
    socket.on('zendesk_configured', (data) => {
      if (data.success) {
        setZendeskConfigured(true);
        console.log('Zendesk configured successfully');
      } else {
        setZendeskConfigured(false);
        addSystemMessage(`Zendesk configuration failed: ${data.error}`, 'error');
      }
    });

    // Ticket creation response
    socket.on('ticket_created', (data) => {
      setIsConnectingToAgent(false);
      if (data.success) {
        setTicketId(data.ticket_id);
        setIsHumanMode(true);
        addSystemMessage(`Connected to human agent! Ticket #${data.ticket_id}`);
        addSystemMessage('You can now chat with our support team.');
      } else {
        addSystemMessage(`Error: ${data.message}`, 'error');
      }
    });

    // Real-time agent responses
    socket.on('agent_response', (data) => {
      console.log('Agent response received:', data);
      addAgentMessage(data.author, data.message, data.role);
    });

    // Ticket closed
    socket.on('ticket_closed', (data) => {
      if (data.success) {
        setTicketId(null);
        setIsHumanMode(false);
        addSystemMessage('Support session ended. You\'re now back to AI assistant.');
      } else {
        addSystemMessage(`Error closing ticket: ${data.error}`, 'error');
      }
    });

    // Message errors
    socket.on('message_error', (data) => {
      addSystemMessage(`Message error: ${data.error}`, 'error');
    });

    // Cleanup on unmount
    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  // Helper functions to add messages
  const addSystemMessage = (text, type = 'info') => {
    const systemMessage = {
      id: Date.now() + Math.random(),
      text,
      sender: 'system',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      systemType: type
    };
    setMessages(prev => [...prev, systemMessage]);
  };

  const addAgentMessage = (author, text, role) => {
    const agentMessage = {
      id: Date.now() + Math.random(),
      text: `${author}${role ? ` (${role})` : ''}: ${text}`,
      sender: 'agent',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, agentMessage]);
  };

  // Connect to human agent
  const connectToHumanAgent = () => {
    // Check credentials first and show modal if not available
    if (!credentialsAvailable) {
      setShowCredentialsModal(true);
      return;
    }

    if (!isConnected) {
      addSystemMessage('Unable to connect to support system. Please try again.', 'error');
      return;
    }

    if (!zendeskConfigured) {
      addSystemMessage('Zendesk credentials not configured properly. Please check your settings.', 'error');
      return;
    }

    if (ticketId) {
      addSystemMessage('You are already connected to a human agent.', 'warning');
      return;
    }

    setIsConnectingToAgent(true);
    addSystemMessage('Connecting you to a human agent...');
    
    socketRef.current.emit('create_ticket', {
      subject: `Support Request - ${new Date().toLocaleString()}`,
      description: 'Customer requested human agent support via chat interface'
    });
  };

  // End human agent session
  const endHumanSession = () => {
    if (!ticketId) {
      addSystemMessage('No active support session found.', 'warning');
      return;
    }
    socketRef.current.emit('close_ticket');
  };

  const handleStreamResponse = async (reader, question, currentIndex) => {
    const decoder = new TextDecoder("utf-8");
    let accumulatedResponse = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split(/\n+/).filter(Boolean);

      for (const line of lines) {
        if (line.startsWith("data:")) {
          const jsonPart = line.slice("data:".length).trim();
          try {
            const parsed = JSON.parse(jsonPart);
            if (parsed.chunk) {
              accumulatedResponse += parsed.chunk;
              setMessages((prevMessages) => {
                const updatedMessages = [...prevMessages];
                if (updatedMessages[currentIndex]) {
                  updatedMessages[currentIndex] = {
                    ...updatedMessages[currentIndex],
                    text: accumulatedResponse
                  };
                }
                return updatedMessages;
              });
            }
            if (parsed.done) {
              return accumulatedResponse;
            }
          } catch (e) {
            console.error("Error parsing JSON:", jsonPart, e);
          }
        }
      }
    }
  };

  const sendMessage = async () => {
    if (newMessage.trim() === '' || loading) return;

    const userMessage = {
      id: messages.length + 1,
      text: newMessage,
      sender: "user",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, userMessage]);

    const questionText = newMessage;
    setNewMessage('');

    // Maintain focus on input after clearing message
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }, 0);

    // If in human mode, send message via socket
    if (isHumanMode && ticketId) {
      socketRef.current.emit('send_message', { message: questionText });
      return;
    }

    // Otherwise, use AI
    const aiMessage = {
      id: messages.length + 2,
      text: "",
      sender: "ai",
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, aiMessage]);

    const currentIndex = messages.length + 1;
    setLoading(true);

    try {
      const response = await make_question_request({
        sessionId: sessionId,
        question: questionText
      });

      if (response.body) {
        const reader = response.body.getReader();
        await handleStreamResponse(reader, questionText, currentIndex);
      }
    } catch (error) {
      console.error("Error:", error);
      setMessages(prev => {
        const updatedMessages = [...prev];
        if (updatedMessages[currentIndex]) {
          updatedMessages[currentIndex] = {
            ...updatedMessages[currentIndex],
            text: "Sorry, I encountered an error. Please try again."
          };
        }
        return updatedMessages;
      });
    } finally {
      setLoading(false);
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !loading && newMessage.trim() !== '') {
      e.preventDefault();
      sendMessage();
    }
  };

  return {
    messages,
    newMessage,
    setNewMessage,
    sendMessage,
    handleKeyPress,
    loading,
    inputRef,
    isHumanMode,
    ticketId,
    connectToHumanAgent,
    endHumanSession,
    isConnectingToAgent,
    showCredentialsModal,
    setShowCredentialsModal
  };
};

const ChatMessage = ({ message }) => {
  // System message styling
  if (message.sender === 'system') {
    const systemType = message.systemType || 'info';
    let bgColor = 'bg-gray-600';
    let textColor = 'text-gray-200';
    
    if (systemType === 'error') {
      bgColor = 'bg-red-900';
      textColor = 'text-red-200';
    } else if (systemType === 'warning') {
      bgColor = 'bg-yellow-900';
      textColor = 'text-yellow-200';
    }

    return (
      <div className="flex justify-center mb-4">
        <div className={`max-w-xs md:max-w-md lg:max-w-lg rounded-lg px-4 py-2 ${bgColor} ${textColor} text-center text-sm`}>
          <p>{message.text}</p>
          <p className="text-xs mt-1 opacity-70">{message.timestamp}</p>
        </div>
      </div>
    );
  }

  // Agent message styling (similar to AI but with different color)
  if (message.sender === 'agent') {
    return (
      <div className="flex justify-start mb-4">
        <div className="max-w-xs md:max-w-md lg:max-w-lg rounded-lg px-4 py-2 bg-green-700 text-white rounded-bl-none">
          <p>{message.text}</p>
          <p className="text-xs mt-1 text-green-200">{message.timestamp}</p>
        </div>
      </div>
    );
  }

  // Regular user/AI messages
  return (
    <div className={`flex mb-4 ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-xs md:max-w-md lg:max-w-lg rounded-lg px-4 py-2 ${
          message.sender === 'user'
            ? 'bg-indigo-600 text-white rounded-br-none'
            : 'bg-gray-700 text-white rounded-bl-none'
        }`}
      >
        <p>{message.text || (
          message.sender === 'ai' && (
            <div className="flex items-center gap-2">
              <div
                className="h-2 w-2 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
              />
              <div
                className="h-2 w-2 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: "150ms" }}
              />
              <div
                className="h-2 w-2 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: "300ms" }}
              />
            </div>
          )
        )}</p>
        <p className={`text-xs mt-1 ${message.sender === 'user' ? 'text-indigo-200' : 'text-gray-400'}`}>
          {message.timestamp}
        </p>
      </div>
    </div>
  );
};

const ChatInput = ({ newMessage, setNewMessage, sendMessage, handleKeyPress, loading, inputRef }) => {
  return (
    <div className="bg-gray-800 p-4 border-t border-gray-700">
      <div className="flex items-center">
        <div className="flex-1 mr-2">
          <input
            ref={inputRef}
            type="text"
            className="w-full bg-gray-700 text-white border-none rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={false}
            autoFocus
          />
        </div>
        <button
          className={`${
            loading || !newMessage.trim()
              ? 'bg-gray-600 cursor-not-allowed' 
              : 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer'
          } text-white rounded-full p-2 whitespace-nowrap transition-colors`}
          onClick={sendMessage}
          disabled={loading || !newMessage.trim()}
        >
          {loading ? (
            <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <i className="fas fa-paper-plane"></i>
          )}
        </button>
      </div>
    </div>
  );
};

const TextChat = () => {
  const { 
    messages, 
    newMessage, 
    setNewMessage, 
    sendMessage, 
    handleKeyPress, 
    loading, 
    inputRef,
    isHumanMode,
    ticketId,
    connectToHumanAgent,
    endHumanSession,
    isConnectingToAgent,
    showCredentialsModal,
    setShowCredentialsModal
  } = useChat();

  const messagesEndRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userName = getUserName();
    if (!token || !userName) {
      console.error("Authentication required for chat functionality");
    }
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: "smooth",
        block: "end"
      });
    }
  }, [messages]);

  return (
    <div className="flex flex-col h-full">
      <div className="bg-gray-800 p-4 border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center">
          <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center mr-3">
            <i className={`fas ${isHumanMode ? 'fa-user-tie' : 'fa-robot'} text-white`}></i>
          </div>
          <div>
            <h3 className="font-medium text-white">
              {isHumanMode ? 'Human Support Agent' : 'Qurve AI Assistant'}
            </h3>
            <p className="text-xs text-gray-400">
              {isHumanMode ? `Online - Ticket #${ticketId}` : 'Online'}
            </p>
          </div>
        </div>
        
        {/* Human Agent Toggle Button */}
        {/* <div className="flex items-center space-x-2">
          {!isHumanMode ? (
            <button
              onClick={connectToHumanAgent}
              disabled={isConnectingToAgent}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                isConnectingToAgent
                  ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {isConnectingToAgent ? (
                <div className="flex items-center space-x-1">
                  <div className="h-3 w-3 border border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Connecting...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-1">
                  <i className="fas fa-user-tie"></i>
                  <span>Connect to Agent</span>
                </div>
              )}
            </button>
          ) : (
            <button
              onClick={endHumanSession}
              className="px-3 py-1 rounded-full text-xs font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
            >
              <div className="flex items-center space-x-1">
                <i className="fas fa-times"></i>
                <span>End Session</span>
              </div>
            </button>
          )}
        </div> */}
      </div>
      
      <div className="flex-1 p-4 overflow-y-auto bg-gray-900" style={{minHeight: "calc(100vh - 240px)"}}>
        {messages.map(message => (
          <ChatMessage key={message.id} message={message} />
        ))}
        <div ref={messagesEndRef} />
      </div>
      
      <ChatInput 
        newMessage={newMessage}
        setNewMessage={setNewMessage}
        sendMessage={sendMessage}
        handleKeyPress={handleKeyPress}
        loading={loading}
        inputRef={inputRef}
      />

      {/* Credentials Modal */}
      <CredentialsModal 
        isOpen={showCredentialsModal}
        onClose={() => setShowCredentialsModal(false)}
      />
    </div>
  );
};

export default TextChat;
